import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import type { GeoPoint, ServiceAreaBounds } from '@/types/api'
import {
  addPoint,
  canClose,
  closeDraft,
  insertPoint,
  midpoints,
  movePoint,
  removePoint,
  type Draft,
} from './polygonDraft'
import styles from './AreaPricingPage.module.css'

/** One sub-area as the map draws it. */
export interface MapArea {
  id: number
  name: string
  /** "East Cairo · +80" — under the name on the map. */
  detail: string
  boundary: GeoPoint[]
  /** The class carrying its group's colour, from `toneClassForGroup`. */
  toneClass: string
  selected: boolean
  /** The sub-area being reshaped is drawn by the editor layer instead. */
  hidden: boolean
}

/**
 * What a click on the map does: pick a sub-area, add a corner to the shape
 * being drawn, or drop a pin for the trip check.
 */
export type MapMode = 'browse' | 'draw' | 'check'

export interface TripPins {
  pickup: GeoPoint | null
  dropoff: GeoPoint | null
}

export interface AreaMapProps {
  bounds: ServiceAreaBounds
  areas: MapArea[]
  mode: MapMode
  draft: Draft | null
  trip: TripPins
  /** Fitted to whenever it changes — a sub-area picked from the list, a shape opened for editing. */
  focus: ServiceAreaBounds | null
  onSelectArea: (id: number) => void
  onDraftChange: (draft: Draft) => void
  onTripChange: (trip: TripPins) => void
}

const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'

function toLatLng(point: GeoPoint): L.LatLngTuple {
  return [point.lat, point.lng]
}

function fromLatLng(latLng: L.LatLng): GeoPoint {
  return { lat: latLng.lat, lng: latLng.lng }
}

function rectangle(bounds: ServiceAreaBounds): L.LatLngBoundsExpression {
  return [
    [bounds.minLat, bounds.minLng],
    [bounds.maxLat, bounds.maxLng],
  ]
}

/**
 * Cairo on OpenStreetMap, with the sub-areas drawn over it, and the tools to
 * draw and reshape them.
 *
 * Driven imperatively, like the route map: Leaflet's map is one long-lived
 * object created against a real element. Each layer — Cairo's outline, the
 * sub-areas, the shape being edited, the trip pins — is a layer group redrawn
 * by its own effect when its data changes. Event handlers read the latest props
 * through a ref, so the map's own listeners are bound once.
 *
 * Drawing: click to add corners; click the first corner to close the shape.
 * Reshaping: drag a corner; drag or click the small dot halfway along an edge
 * to add a corner there; right-click a corner to remove it.
 */
export function AreaMap(props: AreaMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layersRef = useRef<{
    outline: L.LayerGroup
    areas: L.LayerGroup
    draft: L.LayerGroup
    trip: L.LayerGroup
  } | null>(null)
  const latest = useRef(props)
  latest.current = props

  // ------------------------------------------------------------- the map --
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const map = L.map(container, { zoomControl: true, attributionControl: true })
    L.tileLayer(env.mapTileUrl, { maxZoom: 19, attribution: OSM_ATTRIBUTION }).addTo(map)
    map.fitBounds(rectangle(latest.current.bounds), { padding: [12, 12] })

    layersRef.current = {
      outline: L.layerGroup().addTo(map),
      areas: L.layerGroup().addTo(map),
      draft: L.layerGroup().addTo(map),
      trip: L.layerGroup().addTo(map),
    }
    mapRef.current = map

    map.on('click', (event: L.LeafletMouseEvent) => {
      const { mode, draft, trip, onDraftChange, onTripChange } = latest.current
      const point = fromLatLng(event.latlng)

      if (mode === 'draw' && draft && !draft.closed) {
        onDraftChange(addPoint(draft, point))
      } else if (mode === 'check') {
        // Pickup, then drop-off; a third click starts a new trip.
        onTripChange(trip.pickup && !trip.dropoff ? { pickup: trip.pickup, dropoff: point } : { pickup: point, dropoff: null })
      }
    })

    // The panel is resized by the window and by the side panel opening and
    // closing; without this Leaflet keeps drawing into the old size.
    const frame = window.requestAnimationFrame(() => map.invalidateSize())
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(() => map.invalidateSize()) : null
    observer?.observe(container)

    return () => {
      window.cancelAnimationFrame(frame)
      observer?.disconnect()
      map.remove()
      mapRef.current = null
      layersRef.current = null
    }
  }, [])

  // A double click zooms while browsing, and would only get in the way of
  // placing corners and pins.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (props.mode === 'browse') map.doubleClickZoom.enable()
    else map.doubleClickZoom.disable()
  }, [props.mode])

  // ------------------------------------------------------ Cairo's outline --
  const { minLat, minLng, maxLat, maxLng } = props.bounds
  useEffect(() => {
    const layer = layersRef.current?.outline
    if (!layer) return
    layer.clearLayers()
    L.rectangle(rectangle({ minLat, minLng, maxLat, maxLng }), {
      className: styles.serviceArea,
      weight: 2,
      dashArray: '8 8',
      fill: false,
      interactive: false,
    }).addTo(layer)
  }, [minLat, minLng, maxLat, maxLng])

  // ------------------------------------------------------- the sub-areas --
  useEffect(() => {
    const layer = layersRef.current?.areas
    if (!layer) return
    layer.clearLayers()

    // Only clickable while browsing: in the other modes a click on a sub-area
    // is a corner or a pin, and has to reach the map.
    const interactive = props.mode === 'browse'

    for (const area of props.areas) {
      if (area.hidden || area.boundary.length < 3) continue

      const polygon = L.polygon(area.boundary.map(toLatLng), {
        className: cn(styles.areaShape, area.toneClass, area.selected && styles.areaShapeSelected),
        weight: area.selected ? 3 : 2,
        interactive,
      })

      // Built as elements, not an HTML string: Leaflet sets string tooltip
      // content as HTML, and names are typed by people.
      const label = document.createElement('div')
      label.className = styles.areaLabelText
      const name = document.createElement('strong')
      name.textContent = area.name
      const detail = document.createElement('span')
      detail.textContent = area.detail
      label.append(name, detail)
      polygon.bindTooltip(label, {
        permanent: true,
        direction: 'center',
        className: styles.areaLabel,
        opacity: 1,
      })

      if (interactive) {
        polygon.on('click', (event) => {
          L.DomEvent.stopPropagation(event)
          latest.current.onSelectArea(area.id)
        })
      }
      polygon.addTo(layer)
    }
  }, [props.areas, props.mode])

  // -------------------------------------------- the shape being edited --
  useEffect(() => {
    const layer = layersRef.current?.draft
    if (!layer) return
    layer.clearLayers()

    const draft = props.draft
    if (!draft || draft.points.length === 0) return

    const outline = draft.closed
      ? L.polygon(draft.points.map(toLatLng), { className: styles.draftShape, weight: 2, interactive: false })
      : L.polyline(draft.points.map(toLatLng), {
          className: styles.draftLine,
          weight: 2,
          dashArray: '6 6',
          interactive: false,
        })
    outline.addTo(layer)

    const closable = canClose(draft)

    draft.points.forEach((point, index) => {
      const first = index === 0
      const marker = L.marker(toLatLng(point), {
        icon: L.divIcon({
          className: '',
          html: `<span class="${cn(styles.vertex, first && closable && styles.vertexClosable)}"></span>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
        draggable: true,
        keyboard: false,
        title:
          first && closable
            ? 'Click to close the shape'
            : 'Drag to move this corner · right-click to remove it',
      })

      // While dragging, move the outline with the corner; commit on release.
      marker.on('drag', (event) => {
        const moved = draft.points.slice()
        moved[index] = fromLatLng((event.target as L.Marker).getLatLng())
        outline.setLatLngs(moved.map(toLatLng))
      })
      marker.on('dragend', (event) => {
        const current = latest.current.draft
        if (current) latest.current.onDraftChange(movePoint(current, index, fromLatLng((event.target as L.Marker).getLatLng())))
      })
      marker.on('click', () => {
        const current = latest.current.draft
        if (first && current && canClose(current)) latest.current.onDraftChange(closeDraft(current))
      })
      marker.on('contextmenu', (event) => {
        L.DomEvent.preventDefault(event.originalEvent)
        const current = latest.current.draft
        if (current) latest.current.onDraftChange(removePoint(current, index))
      })
      marker.addTo(layer)
    })

    // A dot halfway along each edge: click or drag it to add a corner there.
    for (const mid of midpoints(draft)) {
      const handle = L.marker(toLatLng(mid.point), {
        icon: L.divIcon({
          className: '',
          html: `<span class="${styles.midpoint}"></span>`,
          iconSize: [10, 10],
          iconAnchor: [5, 5],
        }),
        draggable: true,
        keyboard: false,
        title: 'Click or drag to add a corner here',
      })
      handle.on('click', () => {
        const current = latest.current.draft
        if (current) latest.current.onDraftChange(insertPoint(current, mid.afterIndex, mid.point))
      })
      handle.on('dragend', (event) => {
        const current = latest.current.draft
        if (current) {
          latest.current.onDraftChange(
            insertPoint(current, mid.afterIndex, fromLatLng((event.target as L.Marker).getLatLng())),
          )
        }
      })
      handle.addTo(layer)
    }
  }, [props.draft])

  // ------------------------------------------------------ the trip pins --
  const { pickup, dropoff } = props.trip
  useEffect(() => {
    const layer = layersRef.current?.trip
    if (!layer) return
    layer.clearLayers()

    const pins: [GeoPoint | null, 'pickup' | 'dropoff'][] = [
      [pickup, 'pickup'],
      [dropoff, 'dropoff'],
    ]
    for (const [point, kind] of pins) {
      if (!point) continue
      const marker = L.marker(toLatLng(point), {
        icon: L.divIcon({
          className: '',
          html: `<span class="${cn(styles.pin, kind === 'pickup' ? styles.pinPickup : styles.pinDropoff)}"></span>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
        draggable: true,
        keyboard: false,
        title: kind === 'pickup' ? 'Pickup — drag to move' : 'Drop-off — drag to move',
      })
      marker.on('dragend', (event) => {
        const moved = fromLatLng((event.target as L.Marker).getLatLng())
        const trip = latest.current.trip
        latest.current.onTripChange(kind === 'pickup' ? { ...trip, pickup: moved } : { ...trip, dropoff: moved })
      })
      marker.addTo(layer)
    }
  }, [pickup, dropoff])

  // --------------------------------------------------------------- focus --
  useEffect(() => {
    const map = mapRef.current
    const focus = props.focus
    if (!map || !focus) return
    map.fitBounds(rectangle(focus), { padding: [48, 48], maxZoom: 15 })
  }, [props.focus])

  // The mode's class goes on a wrapper, never on the element Leaflet owns:
  // Leaflet adds its own classes to that element, and React rewriting its
  // className when the mode changed wiped them — the tiles and every drawn
  // line vanished the moment drawing started.
  return (
    <div className={cn(styles.mapFrame, props.mode !== 'browse' && styles.mapFramePlacing)}>
      <div ref={containerRef} className={styles.mapCanvas} role="application" aria-label="Map of Cairo's sub-areas" />
    </div>
  )
}
